import React from "react";
import { useTranslation } from "react-i18next";
import SkeletonBlock from "./_shared/SkeletonBlock";
import {
  topItem,
  sumValues,
  fmt,
  trendFor,
  priorPeriodLabel,
  pctTotalKpi,
} from "./_shared/kpiHelpers";

// percentOnly: Global Diamond Chart page only (Task 2344 - switch in
// reports/_shared/globalReportSettings.js).
const ReportKpiBand = ({ report, loading, dataRangeFrom, dataRangeTo, percentOnly }) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="edr-kpis">
        {Array.from({ length: percentOnly ? 5 : 6 }).map((_, index) => (
          <SkeletonBlock className="edr-kpi-skeleton" key={index} />
        ))}
      </div>
    );
  }

  const totalClicks = report?.TotalDiamondClicks?.[0]?.TotalClickCnt || 0;
  const totalSearches = sumValues(report?.DiamondShapeSearches, "CNT");
  const topShape = topItem(report?.DiamondShapeSearches, "Cut", "CNT");
  const topColor = topItem(report?.DiamondColorSearches, "Color", "CNT");
  const topPrice = topItem(report?.DiamondPrice, "Range", "TOTAL");
  const topSize = topItem(report?.DiamondSizeSearches, "Size", "CNT");

  const clicksTrend = trendFor(report?.DiamondSummaryKPIs, "TotalClicks");
  const clicksRow = (report?.DiamondSummaryKPIs || []).find((row) => row.Metric === "TotalClicks");
  const searchesTrend = trendFor(report?.DiamondSummaryKPIs, "TotalSearches");
  const priorLabel = priorPeriodLabel(dataRangeFrom, dataRangeTo);

  // Percent-only: no raw counts - Total Clicks shows the change vs the prior
  // period (never a fixed 100%), "Diamond Searches" (a count) is hidden and
  // every "Top" card shows its share only.
  const kpis = percentOnly
    ? [
        { lab: t("Total Clicks"), ...pctTotalKpi(clicksRow ? clicksRow.CurrentValue : totalClicks, clicksRow ? clicksRow.PriorValue : null) },
        { lab: t("Top Shape"), val: topShape?.label, sub: topShape && `${topShape.percentage}% ${t("of searches")}` },
        { lab: t("Top Price Band"), val: topPrice?.label, sub: topPrice && `${topPrice.percentage}% ${t("of clicks")}` },
        { lab: t("Most Searched Size"), val: topSize?.label, sub: topSize && `${topSize.percentage}%` },
        { lab: t("Top Color"), val: topColor?.label, sub: topColor && `${topColor.percentage}%` },
      ]
    : [
    { lab: t("Total Clicks"), val: fmt(totalClicks), trend: clicksTrend, showPrior: true },
    { lab: t("Diamond Searches"), val: fmt(totalSearches), trend: searchesTrend, showPrior: true },
    {
      lab: t("Top Shape"),
      val: topShape?.label,
      sub: topShape && `${fmt(topShape.value)} ${t("searches")} · ${topShape.percentage}%`,
    },
    {
      lab: t("Top Price Band"),
      val: topPrice?.label,
      sub: topPrice && `${fmt(topPrice.value)} ${t("clicks")} · ${topPrice.percentage}%`,
    },
    {
      lab: t("Most Searched Size"),
      val: topSize?.label,
      sub: topSize && `${fmt(topSize.value)} · ${topSize.percentage}%`,
    },
    {
      lab: t("Top Color"),
      val: topColor?.label,
      sub: topColor && `${fmt(topColor.value)} · ${topColor.percentage}%`,
    },
  ];

  return (
    <div className="edr-kpis">
      {kpis.map((kpi, index) => (
        <div className="edr-kpi" key={index}>
          <div className="edr-kpi-lab">{kpi.lab}</div>
          <div className="edr-kpi-val">{kpi.val ?? "—"}</div>
          {kpi.sub && <div className="edr-kpi-sub">{kpi.sub}</div>}
          {(kpi.trend || (kpi.showPrior && priorLabel)) && (
            <div
              className={`edr-kpi-trend ${
                kpi.trend ? (kpi.trend.up ? "edr-up" : "edr-down") : ""
              }`}
            >
              {kpi.trend && (
                <>
                  {kpi.trend.up ? "▲" : "▼"} {Math.abs(kpi.trend.change).toFixed(2)}%{" "}
                </>
              )}
              {t("vs prior period")}
            </div>
          )}
          {kpi.showPrior && priorLabel && (
            <div className="edr-kpi-prior-range">{priorLabel}</div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ReportKpiBand;
